import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';

import { ZardButtonComponent } from '@solvace/zard/shared/components/button';
import { ZardDialogRef } from '@solvace/zard/shared/components/dialog/dialog-ref';
import { Z_MODAL_DATA } from '@solvace/zard/shared/components/dialog/dialog.service';
import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';

export type SvzConfirmDialogOkType = 'default' | 'solid-destructive' | 'warning';

export interface SvzConfirmDialogData {
  text: string;
  title?: string;
  okText?: string;
  cancelText?: string;
  hideCloseButton?: boolean;
  okType?: SvzConfirmDialogOkType;
  cancelType?: 'default' | 'outline-primary';
  panelClasses?: string;
  titleClasses?: string;
  textClasses?: string;
  buttonClasses?: string;
  actionsClasses?: string;
}

@Component({
  selector: 'svz-confirm-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ZardButtonComponent],
  templateUrl: './svz-confirm-dialog.component.html',
})
export class SvzConfirmDialogComponent {
  private readonly dialogRef = inject(ZardDialogRef<SvzConfirmDialogComponent, boolean | undefined>);
  private readonly data = inject<SvzConfirmDialogData>(Z_MODAL_DATA);
  private readonly translocoService = inject(TranslocoService);

  protected readonly title = this.data?.title ?? this.translocoService.translate('Confirmação');
  protected readonly text = this.data?.text;
  protected readonly okText = this.data?.okText ?? this.translocoService.translate('OK');
  protected readonly cancelText = this.data?.cancelText ?? this.translocoService.translate('Cancelar');
  protected readonly hideCloseButton = this.data?.hideCloseButton ?? false;
  protected readonly okType = this.data?.okType ?? 'default';
  protected readonly cancelType = this.data?.cancelType === 'outline-primary' ? 'outline-primary' : 'outline';

  protected readonly panelClasses = mergeClasses('flex flex-col gap-4 p-1', this.data?.panelClasses);
  protected readonly titleClasses = mergeClasses('text-lg font-semibold tracking-tight', this.data?.titleClasses);
  protected readonly textClasses = mergeClasses('text-sm text-muted-foreground whitespace-pre-wrap', this.data?.textClasses);
  protected readonly buttonClasses = mergeClasses('', this.data?.buttonClasses);
  protected readonly actionsClasses = mergeClasses('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', this.data?.actionsClasses);

  ok(): void {
    this.dialogRef.close(true);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
